/**
 * API-key management.
 *
 * The one-time key dialog is the security-critical part of this screen. The plaintext
 * A secret key exists in exactly one API response and is held only in component state for as long
 * as the dialog is open. It is never written to `localStorage` or `sessionStorage` -- the shared
 * ESLint configuration forbids those in React code precisely so this cannot happen by accident --
 * never placed in the query cache, and never put in the URL.
 *
 * Closing the dialog drops it. That is not a limitation to apologise for; it is the guarantee, and
 * the copy takes a single click so nobody needs to retype it.
 */
import { useEffect, useRef, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ApiError } from '../../lib/api-client';
import { useApiKeys, useCreateApiKey, useRevokeApiKey, type CreatedApiKey } from '../../lib/api';
import { Button, Card, Field, FormError, StatusBadge, TextInput } from '../../components/ui';

const schema = z.object({
  name: z.string().trim().min(1, 'Required.').max(60, 'Too long.'),
  mode: z.enum(['bearer', 'signed']),
});
type CreateKeyForm = z.infer<typeof schema>;

function formatDate(value: string | null): string {
  if (value === null) return 'Never';
  return new Date(value).toLocaleString();
}

export function ApiKeysRoute() {
  const { data, isLoading } = useApiKeys();
  const createKey = useCreateApiKey();
  const revokeKey = useRevokeApiKey();
  const [created, setCreated] = useState<CreatedApiKey | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm<CreateKeyForm>({
    resolver: zodResolver(schema),
    defaultValues: { mode: 'bearer' },
  });

  const onSubmit = handleSubmit((values) => {
    setFormError(null);
    createKey.mutate({ name: values.name, kind: values.mode === 'signed' ? 'publishable' : 'secret' }, {
      onSuccess: (key) => {
        setCreated(key);
        reset();
      },
      onError: (error) => {
        setFormError(error instanceof ApiError ? error.message : 'Could not create the key.');
      },
    });
  });

  // `useWatch` rather than `watch`: the latter returns a fresh function each render, which the
  // React Compiler cannot memoize, so it opts the whole component out of compilation.
  const mode = useWatch({ control, name: 'mode' });

  const keys = data?.keys ?? [];

  return (
    <>
      <div>
        <h1 className="text-xl font-semibold">API keys</h1>
        <p className="mt-1 text-sm text-neutral-600">
          Two kinds of key, and the value says which.{' '}
          <code className="rounded bg-neutral-100 px-1 py-0.5 text-xs">sk_…</code> is a secret,
          sent as{' '}
          <code className="rounded bg-neutral-100 px-1 py-0.5 text-xs">Authorization: Bearer</code>{' '}
          from your backend.{' '}
          <code className="rounded bg-neutral-100 px-1 py-0.5 text-xs">pk_…</code> is publishable and
          belongs in a signed URL. Neither works in the other&rsquo;s place.
        </p>
      </div>

      <Card>
        <h2 className="text-base font-semibold">Create a key</h2>
        <form onSubmit={(event) => void onSubmit(event)} className="mt-4" noValidate>
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <Field
                label="Name"
                htmlFor="key-name"
                hint="Something you will recognise later, such as “Production”."
                error={errors.name?.message}
              >
                <TextInput
                  id="key-name"
                  placeholder="Production"
                  invalid={errors.name !== undefined}
                  {...register('name')}
                />
              </Field>
            </div>
            <Button type="submit" disabled={createKey.isPending} className="mb-6">
              {createKey.isPending ? 'Creating...' : 'Create key'}
            </Button>
          </div>

          {/*
            The kind is fixed at creation, so the consequence is stated here rather than discovered
            later: there is no screen that changes it, by design. A publishable key travels in the
            URL of every image that embeds it, and allowing a later switch to bearer use would turn
            each of those already-published URLs into a working credential.

            The kind also decides the prefix -- `sk_` or `pk_` -- so the value itself says where it
            belongs, without anyone needing to look it up.
          */}
          <fieldset className="mt-2">
            <legend className="text-sm font-medium">How requests authenticate</legend>
            <div className="mt-2 space-y-2">
              <label className="flex gap-3 text-sm">
                <input type="radio" value="bearer" className="mt-1" {...register('mode')} />
                <span>
                  <span className="font-medium">Secret key</span>
                  <span className="block text-neutral-600">
                    Sent as{' '}
                    <code className="rounded bg-neutral-100 px-1 py-0.5 text-xs">
                      Authorization: Bearer
                    </code>{' '}
                    from your backend. Never put one in a page — it is refused in a signed URL, so
                    it cannot be embedded even by mistake.
                  </span>
                </span>
              </label>
              <label className="flex gap-3 text-sm">
                <input type="radio" value="signed" className="mt-1" {...register('mode')} />
                <span>
                  <span className="font-medium">Publishable key</span>
                  <span className="block text-neutral-600">
                    Safe to embed in an <code className="rounded bg-neutral-100 px-1 py-0.5 text-xs">
                      &lt;img&gt;
                    </code>{' '}
                    tag. Every request must carry a signature, so a URL a visitor copies cannot be
                    altered or reused for anything else. Comes with a signing secret for your server.
                  </span>
                </span>
              </label>
            </div>
            {mode === 'signed' && (
              <p className="mt-3 rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900">
                This key will be refused as a bearer token, and the kind cannot be changed later. To
                switch, create a new key and revoke this one.
              </p>
            )}
          </fieldset>
        </form>
        <FormError message={formError} />
      </Card>

      <Card className="p-0">
        <h2 className="border-b border-neutral-200 px-5 py-4 text-base font-semibold">Your keys</h2>
        {isLoading ? (
          <p className="px-5 py-6 text-sm text-neutral-500">Loading...</p>
        ) : keys.length === 0 ? (
          <p className="px-5 py-6 text-sm text-neutral-500" data-testid="no-keys">
            No API keys yet. Create one above to make your first request.
          </p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wide text-neutral-500">
              <tr className="border-b border-neutral-200">
                <th scope="col" className="px-5 py-2 font-medium">
                  Name
                </th>
                <th scope="col" className="px-5 py-2 font-medium">
                  Key
                </th>
                <th scope="col" className="px-5 py-2 font-medium">
                  Kind
                </th>
                <th scope="col" className="px-5 py-2 font-medium">
                  Status
                </th>
                <th scope="col" className="px-5 py-2 font-medium">
                  Last used
                </th>
                <th scope="col" className="px-5 py-2" />
              </tr>
            </thead>
            <tbody>
              {keys.map((apiKey) => (
                <tr key={apiKey.id} className="border-b border-neutral-100 last:border-0">
                  <td className="px-5 py-3 font-medium">{apiKey.name}</td>
                  {/*
                    A publishable key is shown in full: it is already in the `src` of every image
                    that embeds it, so withholding it protected nothing while costing a rotation and
                    a redeploy whenever one was mislaid. A secret key still shows only fragments,
                    because its plaintext is not stored anywhere to show.
                  */}
                  <td className="px-5 py-3 font-mono text-xs text-neutral-600">
                    {apiKey.key === null ? (
                      <>
                        {apiKey.keyPrefix}…{apiKey.keySuffix}
                      </>
                    ) : (
                      <span className="flex items-center gap-2">
                        <span className="break-all">{apiKey.key}</span>
                        <CopyButton value={apiKey.key} label={`Copy ${apiKey.name}`} />
                      </span>
                    )}
                  </td>
                  <td className="px-5 py-3 text-neutral-600">
                    {apiKey.kind === 'publishable' ? 'Publishable' : 'Secret'}
                  </td>
                  <td className="px-5 py-3">
                    <StatusBadge status={apiKey.status} />
                  </td>
                  <td className="px-5 py-3 text-neutral-600">{formatDate(apiKey.lastUsedAt)}</td>
                  <td className="px-5 py-3 text-right">
                    {apiKey.status === 'active' && (
                      <Button
                        variant="danger"
                        onClick={() => revokeKey.mutate({ id: apiKey.id })}
                        disabled={revokeKey.isPending}
                        aria-label={`Revoke ${apiKey.name}`}
                      >
                        Revoke
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>

      {created !== null && <OneTimeKeyDialog created={created} onClose={() => setCreated(null)} />}
    </>
  );
}

/**
 * Copy one value, reporting success on the control itself.
 *
 * Shared so the list and the dialog behave identically. Clipboard access can be denied, and the
 * value is selectable on screen either way, so a failure is not surfaced as an error.
 */
function CopyButton({ value, label }: { value: string; label: string }) {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <Button variant="secondary" aria-label={label} onClick={() => void copy()}>
      {copied ? 'Copied' : 'Copy'}
    </Button>
  );
}

/**
 * The show-once dialog.
 *
 * Rendered as a modal with `role="dialog"` and `aria-modal`, focus moved to the copy control, and
 * Escape closing it. The wording states plainly that the value cannot be retrieved, because a
 * customer who closes this without copying has to create a replacement key.
 */
function OneTimeKeyDialog({ created, onClose }: { created: CreatedApiKey; onClose: () => void }) {
  const [copied, setCopied] = useState<'key' | 'secret' | null>(null);
  const copyButtonRef = useRef<HTMLButtonElement>(null);

  // Focus moves into the dialog on open, so a keyboard user lands on the copy control rather
  // than being left behind the overlay.
  useEffect(() => {
    copyButtonRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const copy = async (value: string, which: 'key' | 'secret') => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(which);
    } catch {
      // Clipboard access can be denied; the value is selectable on screen either way.
      setCopied(null);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-neutral-900/40 p-6">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="one-time-key-title"
        className="w-full max-w-lg rounded-lg bg-white p-6 shadow-lg"
      >
        <h2 id="one-time-key-title" className="text-lg font-semibold">
          {created.kind === 'publishable' ? 'Copy your signing secret' : 'Copy your API key'}
        </h2>
        {/*
          The two kinds keep different things, so they cannot share this sentence. A secret key is
          not stored at all. A publishable key is stored and listed below; its signing secret is the
          value that exists only in this dialog.
        */}
        {created.kind === 'publishable' ? (
          <p data-testid="one-time-notice" className="mt-2 text-sm text-neutral-700">
            The signing secret for <span className="font-medium">{created.name}</span> is shown only
            now. It is not stored, so it cannot be displayed again. The publishable key stays listed
            on this page. If you lose the secret, create a replacement key and revoke this one.
          </p>
        ) : (
          <p data-testid="one-time-notice" className="mt-2 text-sm text-neutral-700">
            This is the only time <span className="font-medium">{created.name}</span> can be shown.
            We store a keyed digest, not the key itself, so it cannot be displayed again. If you
            lose it, create a replacement and revoke this one.
          </p>
        )}

        {/* A caption for a <code> block, not a form field -- <label> would promise a control. */}
        <p className="mt-4 text-xs font-medium uppercase tracking-wide text-neutral-500">
          {created.kind === 'publishable' ? 'Publishable key (goes in the URL)' : 'Secret key'}
        </p>
        <div className="mt-1 flex items-center gap-2">
          <code
            data-testid="one-time-api-key"
            className="flex-1 overflow-x-auto rounded-md bg-neutral-100 px-3 py-2 font-mono text-xs"
          >
            {created.apiKey}
          </code>
          {/* Named, because two "Copy" buttons in one dialog are indistinguishable to a screen reader. */}
          <Button
            ref={copyButtonRef}
            aria-label="Copy API key"
            onClick={() => void copy(created.apiKey, 'key')}
          >
            {copied === 'key' ? 'Copied' : 'Copy'}
          </Button>
        </div>

        {/*
          Publishable keys only. A secret key is refused in a signed URL, so the API issues it no
          signing secret, and showing an empty slot for one would only invite the question of where
          it went.

          The signing secret follows the same rule as the key, stated in this file's header: held in
          component state only, never stored, never cached, never in a URL. It is derived from the
          key rather than saved, so the API cannot show it again either -- and unlike the key, it is
          the half that must never travel to a browser, since anyone holding it can sign any URL.
        */}
        {created.kind === 'publishable' && (
          <>
            <p className="mt-4 text-xs font-medium uppercase tracking-wide text-neutral-500">
              Signing secret
            </p>
            <div className="mt-1 flex items-center gap-2">
              <code
                data-testid="one-time-signing-secret"
                className="flex-1 overflow-x-auto rounded-md bg-neutral-100 px-3 py-2 font-mono text-xs"
              >
                {created.signingSecret}
              </code>
              <Button
                aria-label="Copy signing secret"
                onClick={() => void copy(created.signingSecret, 'secret')}
              >
                {copied === 'secret' ? 'Copied' : 'Copy'}
              </Button>
            </div>
            <p className="mt-2 text-sm text-neutral-700">
              Keep the signing secret on your server. It signs the query string of an embeddable URL
              — the key above may appear in a page, but this secret must not. Use it exactly as
              shown; it is an opaque value, not something to decode.
            </p>
          </>
        )}

        <div className="mt-6 flex justify-end">
          <Button variant="secondary" onClick={onClose}>
            I have saved it
          </Button>
        </div>
      </div>
    </div>
  );
}
